import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Compass, ArrowRight } from "lucide-react";
import { JOURNEY, type Lang } from "@/lib/journey";

interface RecommendedJourneyProps {
  lang?: Lang;
}

const headers = {
  en: { kicker: "Recommended journey", title: "Your 6-step Ocean Navigator path", subtitle: "Follow the steps in order. Each one prepares the next. Dyslexia-friendly, no rush." },
  fr: { kicker: "Parcours recommandé", title: "Votre parcours Ocean Navigator en 6 étapes", subtitle: "Suivez les étapes dans l'ordre. Chacune prépare la suivante. Adapté à la dyslexie, sans pression." },
  es: { kicker: "Recorrido recomendado", title: "Tu camino Ocean Navigator en 6 etapas", subtitle: "Sigue las etapas en orden. Cada una prepara la siguiente. Adaptado a la dislexia, sin prisa." },
};

const cta = { en: "Start step", fr: "Commencer l'étape", es: "Empezar la etapa" };

export const RecommendedJourney = ({ lang = "fr" }: RecommendedJourneyProps) => {
  const h = headers[lang];

  return (
    <section id="recommended-journey" className="py-20 px-4 bg-gradient-to-b from-background to-primary/5">
      <div className="max-w-4xl mx-auto space-y-8">
        <div className="text-center space-y-3">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-semibold uppercase tracking-wider">
            <Compass className="h-3.5 w-3.5" />
            {h.kicker}
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold">{h.title}</h2>
          <p className="text-muted-foreground max-w-xl mx-auto">{h.subtitle}</p>
        </div>

        <ol className="space-y-3">
          {JOURNEY.map((step, i) => (
            <li key={step.id}>
              <Link to={step.path} className="block group">
                <Card className="transition-all hover:border-primary/50 hover:shadow-md">
                  <CardContent className="p-4 sm:p-5 flex items-center gap-4">
                    <div className="flex flex-col items-center justify-center w-12 h-12 rounded-full bg-primary/10 text-primary shrink-0">
                      <span className="text-[10px] uppercase font-bold opacity-70">Step</span>
                      <span className="text-lg font-bold leading-none">{i + 1}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-base sm:text-lg flex items-center gap-2">
                        <span aria-hidden="true">{step.emoji}</span>
                        {step[lang].title}
                      </p>
                      <p className="text-sm text-muted-foreground">{step[lang].subtitle}</p>
                    </div>
                    <div className="flex items-center gap-2 text-primary text-sm font-medium shrink-0">
                      <span className="hidden sm:inline">{cta[lang]}</span>
                      <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
};
